import {
  Labeled,
  SimpleShowLayout,
  UpdateButton,
  useRecordContext,
  useRefresh,
  useGetOne,
  useGetIdentity,
} from 'react-admin';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import { Box, Button, Typography } from '@mui/material';
import { SectionPaper } from '@/components/SectionPaper';
import { FieldWithAction } from '@/components/FieldWithAction';
import ClearIcon from '@mui/icons-material/Clear';
import { CollapsibleSection } from '@/components/CollapsibleSection';
import { useCallback, useMemo } from 'react';
import { PaidSubscriptionInfo } from './PaidSubscriptionInfo';
import {
  FreeTrialInfo,
  FreeTrialActions,
  FreeTrialResetButton,
} from './FreeTrialInfo';
import { OpenInNew } from '@mui/icons-material';
import { useCreateNavigationTargetPath } from '@/components/useCreateNavigationTargetPath';
import { AccountInfo, AccountInfoActions } from './AccountInfo';
import { FreeSubscriptionInfo } from './FreeSubscriptionInfo';
import { StripeSpecialSubscription } from './StripeSpecialSubscription';
import { format as formatDate } from 'date-fns';
import { GenericRelativeDateDisplay } from '@/components/GenericDateFunctionField';
import { getAppConfig } from '@/config/AppConfig';
import { NukeLegacyFiles } from './NukeLegacyFiles';
import { ForceLogout } from './ForceLogout';
import { AppConfigOverrides } from './AppConfigOverrides';

export const TherapistsCommandPanel = () => {
  const record = useRecordContext();

  const { data: identity } = useGetIdentity();

  return (
    <SimpleShowLayout record={record}>
      <section>
        <Box
          display="inline-flex"
          flexDirection="column"
          gap="1rem"
          sx={{ width: 'max-content' }}
        >
          <SectionPaper>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom="0.5rem"
            >
              <Typography variant="h6">Account Info</Typography>
              <AccountInfoActions />
            </Box>
            <AccountInfo />
          </SectionPaper>

          <SectionPaper>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom="0.5rem"
            >
              <Typography variant="h6">App Usage</Typography>
              <AppUsageActions />
            </Box>
            <UsageData />
          </SectionPaper>

          <SectionPaper>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom="0.5rem"
            >
              <Box display="flex" flexDirection="column" gap="0.25rem">
                <Typography variant="h6">Free Trial</Typography>
                <FreeTrialResetButton />
              </Box>
              <FreeTrialActions />
            </Box>
            <Box display="flex" flexDirection="row">
              <FreeTrialInfo />
            </Box>
          </SectionPaper>

          <SectionPaper>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom="0.5rem"
            >
              <Typography variant="h6">Onboarding</Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap="1rem">
              <Box display="flex" flexDirection="row" gap="2rem">
                <Labeled label="Name">
                  <FieldWithAction
                    value={
                      record.firstName
                        ? `${record.title ? `${record.title} ` : ''}${record.firstName} ${record.lastName}`
                        : null
                    }
                    monospace
                    action={
                      record.firstName ? (
                        <UpdateButton
                          label=""
                          color="warning"
                          variant="text"
                          size="small"
                          mutationMode="pessimistic"
                          confirmTitle="Are you sure?"
                          confirmContent="This will send the Therapist to the Initial Onboarding Screen"
                          icon={<ClearIcon fontSize="small" />}
                          sx={{
                            minWidth: 'auto',
                            padding: '4px',
                            '& .MuiButton-startIcon': {
                              margin: 0,
                            },
                            '& .MuiButton-root': {
                              minWidth: 'auto',
                            },
                          }}
                          data={{
                            title: null,
                            firstName: null,
                            lastName: null,
                            lastUpdatedAt: new Date(),
                            lastUpdatedBy: identity!.id,
                          }}
                        />
                      ) : undefined
                    }
                  />
                </Labeled>
                <Labeled label="Email">
                  <FieldWithAction
                    value={record.email || null}
                    monospace
                    action={
                      record.email ? (
                        <UpdateButton
                          label=""
                          color="warning"
                          variant="text"
                          size="small"
                          mutationMode="pessimistic"
                          confirmTitle="Are you sure?"
                          confirmContent="This will send the Therapist to the Email Onboarding Screen"
                          icon={<ClearIcon fontSize="small" />}
                          sx={{
                            minWidth: 'auto',
                            padding: '4px',
                            '& .MuiButton-startIcon': {
                              margin: 0,
                            },
                            '& .MuiButton-root': {
                              minWidth: 'auto',
                            },
                          }}
                          data={{
                            signupCompletedAt: null,
                            email: null,
                            lastUpdatedAt: new Date(),
                            lastUpdatedBy: identity!.id,
                          }}
                        />
                      ) : undefined
                    }
                  />
                </Labeled>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ marginBottom: '0.5rem' }}>
                  Sample Notes
                </Typography>
                <Box display="flex" flexDirection="row" gap="2rem" alignItems="center">
                  <Labeled label="Total">
                    <FieldWithAction
                      value={(record.sessionNotes?.sample?.count || 0).toString()}
                      maxWidth="80px"
                      showActionOnHover={false}
                    />
                  </Labeled>
                  <Labeled label="Last">
                    <FieldWithAction
                      value={
                        record.sessionNotes?.sample?.lastCreatedAt ||
                        record.sessionNotes?.sample?.last?.createdAt
                          ? (() => {
                              try {
                                const date = new Date(
                                  record.sessionNotes?.sample?.lastCreatedAt ||
                                    record.sessionNotes?.sample?.last
                                      ?.createdAt,
                                );
                                if (!isNaN(date.getTime())) {
                                  return formatDate(
                                    date,
                                    'MM/dd/yyyy hh:mm:ss a',
                                  );
                                }
                              } catch {
                                // Invalid date
                              }
                              return null;
                            })()
                          : null
                      }
                      emptyText="—"
                      showActionOnHover={false}
                    />
                  </Labeled>
                </Box>
              </Box>
            </Box>
          </SectionPaper>

          <SectionPaper>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom="0.5rem"
            >
              <Typography variant="h6">Special Billing</Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap="1rem">
              <StripeSpecialSubscription />
              <FreeSubscriptionInfo />
            </Box>
          </SectionPaper>

          {record.billing?.currentSubscription && (
            <PaidSubscriptionInfo />
          )}
          <AppConfigOverrides />

          <CollapsibleSection title="Remote Commands">
            <Box display="flex" flexDirection="column" gap="2rem">
              <Box>
                <h3>System Snapshot</h3>
                <SystemSnapshot />
              </Box>
              <Box>
                <h3>Delete Legacy Files</h3>
                <NukeLegacyFiles />
              </Box>
              <Box>
                <h3>Force Logout/Reset</h3>
                <ForceLogout />
              </Box>
            </Box>
          </CollapsibleSection>
        </Box>
      </section>
    </SimpleShowLayout>
  );
};

export const AppUsageActions = () => {
  const therapist = useRecordContext();

  const patientsPath = useCreateNavigationTargetPath(
    'therapistPatients',
    {
      therapistId: therapist.id,
    },
  );

  const sessionNotesPath = useCreateNavigationTargetPath(
    'therapistSessionNotes',
    {
      therapistId: therapist.id,
      isHiddenTestAccount: 'visible',
    },
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      gap="0.5rem"
      marginLeft="2em"
      onClick={e => e.stopPropagation()}
    >
      <Button
        endIcon={<OpenInNew />}
        href={patientsPath}
        variant="outlined"
        autoCapitalize="none"
        size="small"
      >
        Clients
      </Button>
      <Button
        endIcon={<OpenInNew />}
        href={sessionNotesPath}
        variant="outlined"
        autoCapitalize="none"
        size="small"
      >
        Session Notes (Live + Dictation)
      </Button>
    </Box>
  );
};

export const UsageData = () => {
  const therapist = useRecordContext();

  return (
    <>
      <Box marginTop="1.5rem">
        <Box display="flex" flexDirection="column" gap="1.5rem">
          <Box display="flex" flexWrap="wrap" gap="3rem" alignItems="flex-start">
            <Box>
              <Typography variant="h6" sx={{ marginBottom: '0.5rem', minWidth: '200px' }}>
                Clients
              </Typography>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="1rem"
              >
                <Labeled label="Active">
                  <FieldWithAction
                    value={(therapist.patients.active.count || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Connected">
                  <FieldWithAction
                    value={(therapist.patients.connected.count || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Archived">
                  <FieldWithAction
                    value={(therapist.patients.archived.count || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Deleted">
                  <FieldWithAction
                    value={(therapist.patients.deleted.count || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Total" marginLeft="1rem">
                  <FieldWithAction
                    value={(therapist.patients.total || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      borderLeft: '2px solid rgba(255, 255, 255, 0.2)',
                      paddingLeft: '0.75rem',
                    }}
                    typographySx={{
                      fontWeight: 'bold',
                      color: 'rgba(255, 255, 255, 0.95)',
                    }}
                  />
                </Labeled>
              </Box>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ marginBottom: '0.5rem', minWidth: '200px' }}>
                Homework
              </Typography>
              <Box display="flex" flexDirection="row" alignItems="center" gap="1rem">
                <Labeled label="Active">
                  <FieldWithAction
                    value={(therapist.homework.active.count || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Deleted">
                  <FieldWithAction
                    value={(therapist.homework.deleted.count || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Total" marginLeft="1rem">
                  <FieldWithAction
                    value={(therapist.homework.total || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      borderLeft: '2px solid rgba(255, 255, 255, 0.2)',
                      paddingLeft: '0.75rem',
                    }}
                    typographySx={{
                      fontWeight: 'bold',
                      color: 'rgba(255, 255, 255, 0.95)',
                    }}
                  />
                </Labeled>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '2rem',
              alignItems: 'flex-start',
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ marginBottom: '0.5rem', minWidth: '200px' }}>
                Session Notes (total)
              </Typography>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="1rem"
              >
                <Labeled label="Active">
                  <FieldWithAction
                    value={(
                      (therapist.sessionNotes.dictation.active.count || 0) +
                      (therapist.sessionNotes.live.active.count || 0)
                    ).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Deleted">
                  <FieldWithAction
                    value={(
                      therapist.sessionNotes.dictation.deleted.count || 0
                    ).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Total" marginLeft="1rem">
                  <FieldWithAction
                    value={(therapist.sessionNotes.dictation.total || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      borderLeft: '2px solid rgba(255, 255, 255, 0.2)',
                      paddingLeft: '0.75rem',
                    }}
                    typographySx={{
                      fontWeight: 'bold',
                      color: 'rgba(255, 255, 255, 0.95)',
                    }}
                  />
                </Labeled>
              </Box>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ marginBottom: '0.5rem', minWidth: '200px' }}>
                Dictation
              </Typography>
              <Box display="flex" flexDirection="row" alignItems="center" gap="1rem">
                <Labeled label="Active">
                  <FieldWithAction
                    value={(
                      therapist.sessionNotes.dictation.active.count || 0
                    ).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Deleted">
                  <FieldWithAction
                    value={(
                      therapist.sessionNotes.dictation.deleted.count || 0
                    ).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Total" marginLeft="1rem">
                  <FieldWithAction
                    value={(therapist.sessionNotes.dictation.total || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      borderLeft: '2px solid rgba(255, 255, 255, 0.2)',
                      paddingLeft: '0.75rem',
                    }}
                    typographySx={{
                      fontWeight: 'bold',
                      color: 'rgba(255, 255, 255, 0.95)',
                    }}
                  />
                </Labeled>
              </Box>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ marginBottom: '0.5rem', minWidth: '200px' }}>
                Live
              </Typography>
              <Box display="flex" flexDirection="row" alignItems="center" gap="1rem">
                <Labeled label="Active">
                  <FieldWithAction
                    value={(
                      therapist.sessionNotes.live.active.count || 0
                    ).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Deleted">
                  <FieldWithAction
                    value={(
                      therapist.sessionNotes.live.deleted.count || 0
                    ).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Total" marginLeft="1rem">
                  <FieldWithAction
                    value={(therapist.sessionNotes.live.total || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      borderLeft: '2px solid rgba(255, 255, 255, 0.2)',
                      paddingLeft: '0.75rem',
                    }}
                    typographySx={{
                      fontWeight: 'bold',
                      color: 'rgba(255, 255, 255, 0.95)',
                    }}
                  />
                </Labeled>
              </Box>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ marginBottom: '0.5rem', minWidth: '200px' }}>
                Committed Notes
              </Typography>
              <Box display="flex" flexDirection="row" alignItems="center" gap="1rem">
                <Labeled label="Total">
                  <FieldWithAction
                    value={(therapist.sessionNotes?.committed?.count || 0).toString()}
                    maxWidth="80px"
                    showActionOnHover={false}
                  />
                </Labeled>
                <Labeled label="Last">
                  <FieldWithAction
                    value={
                      therapist.sessionNotes?.committed?.lastCreatedAt ||
                      therapist.sessionNotes?.committed?.last?.createdAt
                        ? (() => {
                            try {
                              const date = new Date(
                                therapist.sessionNotes?.committed
                                  ?.lastCreatedAt ||
                                  therapist.sessionNotes?.committed?.last
                                    ?.createdAt,
                              );
                              if (!isNaN(date.getTime())) {
                                return formatDate(
                                  date,
                                  'MM/dd/yyyy hh:mm:ss a',
                                );
                              }
                            } catch {
                              // Invalid date
                            }
                            return null;
                          })()
                        : null
                    }
                    emptyText="—"
                    showActionOnHover={false}
                  />
                </Labeled>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box>{/* <TherapistActivityChart /> */}</Box>
      </Box>
      {/* <MetadataChartsDialog {...{ open, onClose }} /> */}
    </>
  );
};

export const SystemSnapshot = () => {
  const therapist = useRecordContext();

  return <BaseSystemSnapshot therapist={therapist} />;
};

export type BaseSystemSnapshotProps = {
  therapist: any;
};

const appConfig = getAppConfig();

export const BaseSystemSnapshot = ({
  therapist,
}: BaseSystemSnapshotProps) => {
  const systemSnapshot = useMemo(
    () => therapist?.systemSnapshot || {},
    [therapist],
  );

  const onViewOnAzure = useCallback(() => {
    const blobPrefix = appConfig.azureDiagnosticsBlobPrefix;

    const blobName = `${blobPrefix}${therapist.id}`;

    window.open(blobName, '_blank');
  }, [therapist]);

  if (!therapist) {
    return null;
  }

  return (
    <>
      <Box paddingTop="1.5rem" display="flex">
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          paddingTop=".5rem"
        >
          <Labeled label="Last Requested At">
            {systemSnapshot.lastRequestedAt ? (
              <GenericRelativeDateDisplay
                date={systemSnapshot.lastRequestedAt}
              />
            ) : (
              <Typography>Never</Typography>
            )}
          </Labeled>
          <Labeled marginLeft="1rem" label="Last Completed At">
            {systemSnapshot.lastCompletedAt ? (
              <GenericRelativeDateDisplay
                date={systemSnapshot.lastCompletedAt}
              />
            ) : (
              <Typography>Never</Typography>
            )}
          </Labeled>
          <Box marginLeft="1rem">
            <SystemSnapshotButton therapistId={therapist.id as any} />
          </Box>
          {systemSnapshot.lastCompletedAt && (
            <Box marginLeft="1rem">
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={onViewOnAzure}
              >
                View on Azure
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export type SystemSnapshotButtonProps = {
  therapistId: string;
  label?: string;
  confirmTitle?: string;
  confirmContent?: string;
};

export type ForeignEntitySystemSnapshotProps = {
  therapistId: string;
};

export const ForeignEntitySystemSnapshot = ({
  therapistId,
}: ForeignEntitySystemSnapshotProps) => {
  const therapist = useGetOne('therapists', {
    id: therapistId,
    meta: { raw: true },
  });

  if (!therapist) {
    return null;
  }

  return <BaseSystemSnapshot therapist={therapist.data} />;
};

export const SystemSnapshotButton = ({
  therapistId,
  label = 'Request System Snapshot',
  confirmTitle = 'Are you sure?',
  confirmContent = "This will request a system snapshot from the Therapist's device",
}: SystemSnapshotButtonProps) => {
  const refresh = useRefresh();

  const updateData = useMemo(
    () => ({
      id: therapistId!,
      systemSnapshot: JSON.stringify({
        lastRequestedAt: new Date().toISOString(),
        lastCompletedAt: null,
      }),
    }),
    [therapistId],
  );

  const mutationOptions = useMemo(
    () => ({
      onSuccess: refresh,
    }),
    [refresh],
  );

  if (!therapistId) {
    return null;
  }

  return (
    <UpdateButton
      label={label}
      resource="therapists"
      id={therapistId}
      color="warning"
      variant="outlined"
      mutationMode="pessimistic"
      confirmTitle={confirmTitle}
      confirmContent={confirmContent}
      icon={<AutoFixHighIcon />}
      sx={{ marginLeft: '1rem' }}
      mutationOptions={mutationOptions}
      data={updateData}
    />
  );
};
